import {
  AlertTriangle,
  BarChart3,
  FolderTree,
  Images,
  Link2,
  LibraryBig,
  LogOut,
  Menu,
  Plus,
  Settings,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ThemeToggle } from '@/components/theme-toggle';
import { AddLinkDialog } from '@/features/links/add-link-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser } from '@/lib/current-user';
import { useSession } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { initials } from '@/lib/format';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LibraryBig;
  adminOnly?: boolean;
  /** Visible to any signed-in user (viewer or admin), but hidden from anonymous visitors. */
  authOnly?: boolean;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Catalogue', Icon: LibraryBig },
  { to: '/media', label: 'Media', Icon: Images, authOnly: true },
  { to: '/stats', label: 'Statistics', Icon: BarChart3, authOnly: true },
  { to: '/categories', label: 'Categories', Icon: FolderTree, adminOnly: true },
  { to: '/admin/failures', label: 'Failures', Icon: AlertTriangle, adminOnly: true },
  { to: '/settings', label: 'Settings', Icon: Settings, adminOnly: true },
];

function navClasses(isActive: boolean): string {
  return cn(
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    isActive
      ? 'bg-accent text-accent-foreground'
      : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
  );
}

export function AppLayout() {
  const user = useCurrentUser();
  const session = useSession();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [addLinkOpen, setAddLinkOpen] = useState(false);

  const items = NAV_ITEMS.filter((item) => {
    if (item.adminOnly) return user.role === 'admin';
    if (item.authOnly) return user.role !== 'anonymous';
    return true;
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex h-14 w-full max-w-[1400px] items-center gap-3 px-4">
          <NavLink
            to="/"
            className="flex items-center gap-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"
            >
              <Link2 className="h-4 w-4" />
            </span>
            <span className="text-sm font-semibold tracking-tight">SLC</span>
            <span className="sr-only">Seez Links Collector — go to catalogue</span>
          </NavLink>

          <nav aria-label="Primary" className="ml-2 hidden md:block">
            <ul className="flex items-center gap-1">
              {items.map(({ to, label, Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/'}
                    className={({ isActive }) => navClasses(isActive)}
                  >
                    <Icon aria-hidden="true" className="h-4 w-4" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-1">
            {session.bypass ? (
              <Badge variant="warning" title="VITE_LOCAL_AUTH_BYPASS is enabled">
                Local auth bypass
              </Badge>
            ) : null}

            {user.role !== 'anonymous' ? (
              // Any signed-in user, like anyone can share a link in Slack. The API enforces it.
              <Button
                type="button"
                size="sm"
                className="mr-1"
                aria-label="Add a link"
                onClick={() => setAddLinkOpen(true)}
              >
                <Plus aria-hidden="true" className="h-4 w-4" />
                <span className="hidden sm:inline">Add link</span>
              </Button>
            ) : null}

            <ThemeToggle />

            {user.role === 'anonymous' ? (
              // Only reachable while the catalogue is public — a private one shows anonymous
              // visitors the sign-in page instead. Signing in reveals who shared what and unlocks
              // the admin views.
              <Button type="button" variant="outline" size="sm" onClick={session.signIn}>
                Sign in
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={`Account menu for ${user.email}`}>
                    <span
                      aria-hidden="true"
                      className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground"
                    >
                      {initials(user.name ?? user.email)}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
                  <div className="px-2 pb-2">
                    <p className="truncate text-sm font-medium">{user.name ?? user.email}</p>
                    <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                    <Badge variant="secondary" className="mt-2">
                      {user.role === 'admin' ? 'Administrator' : 'Viewer'}
                    </Badge>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={session.signOut}>
                    <LogOut aria-hidden="true" className="h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav"
              aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <Menu aria-hidden="true" className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {mobileNavOpen ? (
          <nav
            id="mobile-nav"
            aria-label="Primary (mobile)"
            className="border-t border-border md:hidden"
          >
            <ul className="mx-auto flex w-full max-w-[1400px] flex-col gap-1 p-2">
              {items.map(({ to, label, Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/'}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) => navClasses(isActive)}
                  >
                    <Icon aria-hidden="true" className="h-4 w-4" />
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}
        <div aria-hidden="true" className="seez-spectrum" />
      </header>

      {user.role !== 'anonymous' ? (
        <AddLinkDialog open={addLinkOpen} onOpenChange={setAddLinkOpen} />
      ) : null}

      <main id="main-content" className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:py-8">
        <Outlet />
      </main>

      <footer className="border-t border-border py-6">
        <div className="mx-auto w-full max-w-[1400px] px-4 text-xs text-muted-foreground">
          SLC keeps one record per unique link, owned by the first person who shared it.
        </div>
      </footer>
    </div>
  );
}
